import React, { useState, useEffect } from 'react';
import { useSnackbar } from '../../contexts/SnackbarContext';
import planningTaskService, { PlanningTask } from '../../services/planningTaskService';
import pdcaService, { PDCAMetrics } from '../../services/pdcaService';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  CheckCircle as PlanIcon,
  PlayArrow as DoIcon,
  Assessment as CheckIcon,
  EmojiEvents as ActIcon,
  ArrowForward as TransitionIcon,
  TrendingUp as ProgressIcon,
  Block as BlockerIcon,
  Lightbulb as LessonIcon,
  Timer as TimerIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

const PDCADashboard: React.FC = () => {
  const [tasks, setTasks] = useState<PlanningTask[]>([]);
  const [metrics, setMetrics] = useState<PDCAMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState<PlanningTask | null>(null);
  const [transitionDialogOpen, setTransitionDialogOpen] = useState(false);
  const [targetPhase, setTargetPhase] = useState<'plan' | 'do' | 'check' | 'act'>('plan');
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [tasksResponse, metricsData] = await Promise.all([
        planningTaskService.getTasks(),
        pdcaService.getMetrics()
      ]);
      setTasks(tasksResponse.data);
      setMetrics(metricsData);
    } catch (error: any) {
      console.error('Error loading data:', error);
      showSnackbar(error.response?.data?.error || 'Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getTasksByPhase = (phase: 'plan' | 'do' | 'check' | 'act') => {
    return tasks.filter(task => task.pdcaPhase === phase);
  };

  const handleTransitionClick = (task: PlanningTask, phase: 'plan' | 'do' | 'check' | 'act') => {
    setSelectedTask(task);
    setTargetPhase(phase);
    setTransitionDialogOpen(true);
  };

  const handleTransition = async () => {
    if (!selectedTask) return;

    try {
      await pdcaService.transitionPhase(selectedTask._id!, targetPhase);
      showSnackbar(`Task transitioned to ${targetPhase.toUpperCase()} phase`, 'success');
      setTransitionDialogOpen(false);
      await loadData();
    } catch (error: any) {
      console.error('Error transitioning task:', error);
      showSnackbar(error.response?.data?.error || 'Failed to transition task', 'error');
    }
  };

  const getPhaseColor = (phase: string) => {
    const colors = {
      plan: '#003366',
      do: '#0071BC',
      check: '#FF8C00',
      act: '#2E7D32'
    };
    return colors[phase as keyof typeof colors] || '#003366';
  };

  const getPhaseIcon = (phase: string) => {
    const icons = {
      plan: <PlanIcon />,
      do: <DoIcon />,
      check: <CheckIcon />,
      act: <ActIcon />
    };
    return icons[phase as keyof typeof icons] || <PlanIcon />;
  };

  const getPhaseCount = (phase: string) => {
    return metrics?.phaseDistribution.find(p => p._id === phase)?.count || 0;
  };

  const getPhaseProgress = (phase: string) => {
    return metrics?.phaseDistribution.find(p => p._id === phase)?.avgProgress || 0;
  };

  const getPriorityColor = (priority: string) => {
    const colors = {
      high: '#B31B1B',
      medium: '#FF8C00',
      low: '#2E7D32'
    };
    return colors[priority as keyof typeof colors] || '#FF8C00';
  };

  const renderPhaseColumn = (phase: 'plan' | 'do' | 'check' | 'act', title: string) => {
    const phaseTasks = getTasksByPhase(phase);
    const phaseColor = getPhaseColor(phase);
    const phaseCount = getPhaseCount(phase);
    const phaseProgress = getPhaseProgress(phase);

    return (
      <Grid item xs={12} md={3} key={phase}>
        <Paper
          sx={{
            p: 2,
            bgcolor: phaseColor,
            color: 'white',
            minHeight: '600px',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Phase Header */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              {getPhaseIcon(phase)}
              <Typography variant="h6" sx={{ ml: 1, fontWeight: 700 }}>
                {title}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Chip
                label={`${phaseCount} tasks`}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
              />
              <Tooltip title="Average Progress">
                <Chip
                  icon={<ProgressIcon sx={{ color: 'white !important' }} />}
                  label={`${Math.round(phaseProgress)}%`}
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                />
              </Tooltip>
            </Box>
          </Box>

          {/* Tasks */}
          <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
            {phaseTasks.length === 0 ? (
              <Alert severity="info" sx={{ bgcolor: 'rgba(255,255,255,0.9)' }}>
                No tasks in this phase
              </Alert>
            ) : (
              phaseTasks.map(task => (
                <Card
                  key={task._id}
                  sx={{
                    mb: 2,
                    bgcolor: 'white',
                    '&:hover': {
                      boxShadow: 4,
                      transform: 'translateY(-2px)',
                      transition: 'all 0.2s'
                    }
                  }}
                >
                  <CardContent sx={{ pb: 1 }}>
                    {/* Task Title */}
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: '#003366',
                        mb: 1
                      }}
                    >
                      {task.title}
                    </Typography>

                    {/* Priority & Category */}
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={task.priority}
                        size="small"
                        sx={{
                          bgcolor: getPriorityColor(task.priority),
                          color: 'white',
                          fontSize: '0.7rem',
                          height: '20px'
                        }}
                      />
                      <Chip
                        label={task.category}
                        size="small"
                        sx={{
                          bgcolor: '#71767A',
                          color: 'white',
                          fontSize: '0.7rem',
                          height: '20px'
                        }}
                      />
                      <Chip
                        label={task.timeframe}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontSize: '0.7rem',
                          height: '20px'
                        }}
                      />
                    </Box>

                    {/* Progress Bar (for DO phase) */}
                    {phase === 'do' && task.progressPercentage !== undefined && (
                      <Box sx={{ mt: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Progress
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {task.progressPercentage}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={task.progressPercentage}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: phaseColor
                            }
                          }}
                        />
                      </Box>
                    )}

                    {/* Blockers Indicator */}
                    {task.blockerHistory && task.blockerHistory.some((b: any) => b.status === 'active') && (
                      <Chip
                        icon={<BlockerIcon />}
                        label="Blocked"
                        size="small"
                        color="error"
                        sx={{ mt: 1 }}
                      />
                    )}
                  </CardContent>

                  {/* Actions */}
                  <CardActions sx={{ pt: 0, px: 2, pb: 1, justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary">
                      {task.status}
                    </Typography>
                    <Box>
                      {phase !== 'act' && (
                        <Tooltip title={`Move to ${phase === 'plan' ? 'DO' : phase === 'do' ? 'CHECK' : 'ACT'}`}>
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleTransitionClick(
                                task,
                                phase === 'plan' ? 'do' : phase === 'do' ? 'check' : 'act'
                              )
                            }
                            sx={{ color: phaseColor }}
                          >
                            <TransitionIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </CardActions>
                </Card>
              ))
            )}
          </Box>
        </Paper>
      </Grid>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Paper sx={{ p: 4, mb: 3, bgcolor: '#003366', color: 'white' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography
                variant="h3"
                component="h1"
                sx={{ fontWeight: 700, mb: 1, color: '#0071BC' }}
                aria-label="PDCA Workflow Dashboard"
              >
                PDCA Workflow Dashboard
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ color: 'white' }}
                aria-label="Plan Do Check Act Continuous Improvement"
              >
                Plan-Do-Check-Act Continuous Improvement Cycle
              </Typography>
              <Chip
                label="Strategic Task Management"
                sx={{
                  mt: 2,
                  bgcolor: '#B31B1B',
                  color: 'white',
                  fontWeight: 600
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={loadData}
                sx={{ bgcolor: 'white', color: '#003366', '&:hover': { bgcolor: '#f0f0f0' } }}
              >
                Refresh
              </Button>
              {metrics && (
                <Tooltip title="Average Cycle Time">
                  <Chip
                    icon={<TimerIcon sx={{ color: 'white !important' }} />}
                    label={`${Math.round(metrics.avgCycleTime)}h avg`}
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                  />
                </Tooltip>
              )}
            </Box>
          </Box>
        </Paper>

        {/* PDCA Columns */}
        <Grid container spacing={2}>
          {renderPhaseColumn('plan', 'PLAN')}
          {renderPhaseColumn('do', 'DO')}
          {renderPhaseColumn('check', 'CHECK')}
          {renderPhaseColumn('act', 'ACT')}
        </Grid>

        {/* Transition Dialog */}
        <Dialog
          open={transitionDialogOpen}
          onClose={() => setTransitionDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ bgcolor: '#003366', color: 'white' }}>
            Transition Task to {targetPhase.toUpperCase()} Phase
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to transition this task to the <strong>{targetPhase.toUpperCase()}</strong> phase?
            </Typography>
            {selectedTask && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <strong>{selectedTask.title}</strong>
                <br />
                Current Phase: {selectedTask.pdcaPhase?.toUpperCase()}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setTransitionDialogOpen(false)} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={handleTransition}
              variant="contained"
              startIcon={<TransitionIcon />}
              sx={{ bgcolor: getPhaseColor(targetPhase) }}
            >
              Transition
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default PDCADashboard;
